<template>
  <div>
    <component :is="inner" v-if="stage === 'running'" />
    <div v-else class="proctoring-gate">
      <p v-if="stage === 'loading'">{{ $t("proctoring_loading") }}</p>
      <p v-else-if="stage === 'finished'">{{ $t("proctoring_finished") }}</p>
      <template v-else>
        <p>{{ errorText }}</p>
        <a href="#" class="courses-test-start" @click.prevent="setup">
          {{ $t("proctoring_retry") }}
        </a>
      </template>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const SDK_ELEMENT_ID = "proctoring-sdk";

export default {
  name: "ProctoringGate",
  // Компонент теста передаётся пропом, а не слотом: при v-if на <component>
  // экземпляр не создаётся, пока сеанс не запущен, — значит beforeCreate
  // страницы теста не сработает и вопросы не будут запрошены раньше времени.
  props: {
    inner: { type: [Object, Function], required: true },
  },
  data() {
    return {
      stage: "loading", // loading | running | finished | error
      errorCode: null,
      identifier: null,
      supervisor: null,
    };
  },
  computed: {
    errorText() {
      return this.$t("proctoring_error_" + (this.errorCode || "generic"));
    },
  },
  mounted() {
    this.setup();
    // beforeDestroy срабатывает при переходе внутри сайта, но не при закрытии
    // вкладки. Без pagehide сеанс остаётся started: у провайдера он висит и
    // расходует квоту, а бэкенд до конца quiz.duration продолжает считать его
    // активным — то есть камеру можно закрыть и дорешать тест.
    window.addEventListener("pagehide", this.onPageHide);
  },
  beforeDestroy() {
    window.removeEventListener("pagehide", this.onPageHide);
    this.teardown();
  },
  methods: {
    authHeaders() {
      const token = localStorage.getItem("access");
      return { Authorization: token ? `Bearer ${token}` : "" };
    },

    async setup() {
      this.stage = "loading";
      this.errorCode = null;

      let session;
      try {
        session = (
          await axios.post(
            `${process.env.VUE_APP_API_URL}/api/proctoring/v1/sessions/`,
            { quiz_slug: this.$route.params.slug },
            { headers: this.authHeaders() }
          )
        ).data;
      } catch (e) {
        // Бэкенд недоступен — не блокируем обучение, тест откроется как раньше.
        this.stage = "running";
        return;
      }

      if (!session.enabled) {
        this.stage = "running";
        return;
      }
      this.identifier = session.identifier;

      try {
        await this.loadSdk(session.url);
      } catch (e) {
        this.fail("sdk");
        return;
      }

      try {
        this.supervisor = new window.Supervisor({ url: session.url });
        this.supervisor.on("start", this.onStart);
        this.supervisor.on("stop", this.onStop);
        await this.supervisor.init({ provider: "jwt", token: session.token });
      } catch (e) {
        this.fail("token");
        return;
      }

      try {
        await this.supervisor.start();
      } catch (e) {
        this.fail("permissions");
      }
    },

    loadSdk(baseUrl) {
      if (window.Supervisor) return Promise.resolve();
      return new Promise((resolve, reject) => {
        const existing = document.getElementById(SDK_ELEMENT_ID);
        if (existing) existing.parentNode.removeChild(existing);
        const el = document.createElement("script");
        el.id = SDK_ELEMENT_ID;
        el.src = `${baseUrl}/sdk/supervisor.js`;
        el.onload = resolve;
        el.onerror = () => reject(new Error("sdk"));
        document.head.appendChild(el);
      });
    },

    async onStart() {
      try {
        await axios.post(
          `${process.env.VUE_APP_API_URL}/api/proctoring/v1/sessions/${this.identifier}/started/`,
          {},
          { headers: this.authHeaders() }
        );
      } catch (e) {
        this.fail("generic");
        return;
      }
      this.stage = "running";
    },

    onStop() {
      // SDK сообщил, что наблюдение прекращено — закрываем сеанс сразу,
      // не дожидаясь ухода со страницы.
      this.reportStopped();
      this.stage = "finished";
    },

    fail(code) {
      this.errorCode = code;
      this.stage = "error";
    },

    // Сообщить бэкенду, что сеанс закончился. keepalive обязателен: обычный
    // запрос при закрытии вкладки браузер отменяет. sendBeacon не подходит —
    // он не умеет ставить заголовок Authorization.
    reportStopped() {
      if (!this.identifier) return;
      const url = `${process.env.VUE_APP_API_URL}/api/proctoring/v1/sessions/${this.identifier}/stopped/`;
      this.identifier = null;
      try {
        fetch(url, {
          method: "POST",
          keepalive: true,
          headers: { ...this.authHeaders(), "Content-Type": "application/json" },
          body: "{}",
        }).catch(() => {});
      } catch (e) {
        /* уходящую страницу спасать нечем */
      }
    },

    onPageHide() {
      this.reportStopped();
      const supervisor = this.supervisor;
      this.supervisor = null;
      if (supervisor) {
        try {
          supervisor.stop();
        } catch (e) {
          /* страница уже уходит */
        }
      }
    },

    teardown() {
      this.reportStopped();
      const supervisor = this.supervisor;
      if (!supervisor) return;
      this.supervisor = null;
      Promise.resolve()
        .then(() => supervisor.stop())
        .then(() => supervisor.logout())
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.proctoring-gate {
  max-width: 640px;
  margin: 80px auto;
  padding: 32px;
  text-align: center;
  line-height: 1.6;
}
</style>
